import clsx from 'clsx';
import {
  Control,
  FieldError,
  FieldPath,
  FieldValues,
  get,
  UseFormRegisterReturn,
  useFormState,
} from 'react-hook-form';

import FormError from '../FormError';
import FormLabel from '../FormLabel';

interface Props {
  options: { value: string; label: string }[];
  className?: string;
  name?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  value?: string;
  placeholder?: string;
  register?: UseFormRegisterReturn;
}

function SelectInput({
  className,
  options,
  name,
  disabled,
  onChange,
  value,
  placeholder,
  register,
}: Props): JSX.Element {
  const onChangeHandler =
    onChange &&
    ((event: React.ChangeEvent<HTMLSelectElement>): void => {
      onChange(event.target.value);
    });

  const selectProps = {
    name,
    disabled,
    onChange: onChangeHandler,
    value,
    placeholder,
    ...register,
  };
  return (
    <select
      className={clsx(
        'block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm',
        className,
      )}
      defaultValue=""
      {...selectProps}
    >
      {placeholder && (
        <option disabled value="">
          {placeholder}
        </option>
      )}
      {options.map(({ value: optionValue, label }) => (
        <option key={optionValue} value={optionValue}>
          {label}
        </option>
      ))}
    </select>
  );
}

interface SelectInputLabelledProps extends Props {
  label?: string;
  error?: React.ReactNode;
  selectClassName?: string;
  sublabel?: React.ReactNode;
}

SelectInput.Labelled = function SelectInputLabelled({
  label,
  className,
  error,
  selectClassName,
  sublabel,
  ...rest
}: SelectInputLabelledProps): JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={clsx('block', className)}>
      {label && <FormLabel>{label}</FormLabel>}
      <SelectInput className={selectClassName} {...rest} />
      {error && <FormError>{error}</FormError>}
      {sublabel && <span className="subtext">{sublabel}</span>}
    </label>
  );
};

interface SelectInputLabelledController<T extends FieldValues>
  extends Omit<SelectInputLabelledProps, 'register'> {
  control: Control<T>;
  name: FieldPath<T>;
}

SelectInput.LabelledController = function SelectInputController<
  T extends FieldValues,
>({ name, control, ...rest }: SelectInputLabelledController<T>): JSX.Element {
  const { errors } = useFormState({ name, control });
  const error = get(errors, name) as FieldError | undefined;

  return (
    <SelectInput.Labelled
      register={control.register(name)}
      error={error?.message}
      {...rest}
    />
  );
};

export default SelectInput;
